import { TextField } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isUserAuthorizedByServer } from "../api/userAccess";
import AdminDashboard from "../components/dashboard/AdminDashboard.jsx";
import EmployeeDashboard from "../components/dashboard/EmployeeDashboard.jsx";
import StarsSelectInput from "../components/fundamentals/StarsSelectInput.jsx";
import PageLoading from "../components/PageLoading";
import {
  selectStationFilter,
  selectUserSession,
  selectWorkgroupFilters,
  updateStationFilter,
  updateWorkgroupFilters,
} from "../slices/userSession/userSessionSlice";
import { logout } from "../util/authentication";
import {
  getAdminStation,
  getEmployeeStation,
  getMultiStationAdminStationList,
  sortOnCaseSensitiveTexts,
} from "../util/commonUtils.js";
import { ALL_WORKGROUP_DEFAULT_FILTERS, MULTI_STATION_ADMIN_ACCESS, STATION } from "../util/constants.js";
import { STATION_FILTER_MUST_BE_SELECTED, WORKGROUP_FILTER_MUST_BE_SELECTED } from "../util/messageConstants.js";

const HomePage = (props) => {
  const dispatch = useDispatch();
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const workgroupFilters = useSelector(selectWorkgroupFilters);
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const TABS = Object.freeze({
    MY_DASHBOARD: 0,
    ADMIN_DASHBOARD: 1,
  });
  const [activeTab, setActiveTab] = useState(userSession.isAdmin ? TABS.ADMIN_DASHBOARD : TABS.MY_DASHBOARD);

  const stationFilterRef = useRef("");

  useEffect(() => {
    const isUserAuthorized = () => {
      isUserAuthorizedByServer(userSession).then((is200) => {
        if (is200) {
          setIsUserAuthorizedState(true);
        } else {
          logout().then();
        }
        setIsLoading(false);
      });
    };

    if (!isUserAuthorizedState) {
      isUserAuthorized();
    }
  }, [isUserAuthorizedState, userSession]);

  useEffect(() => {
    if (activeTab === TABS.MY_DASHBOARD) {
      dispatch(updateStationFilter(getEmployeeStation(props.featureConfig.userStations, userSession, stationFilter)));
    } else if (activeTab === TABS.ADMIN_DASHBOARD) {
      dispatch(updateStationFilter(getAdminStation(props.featureConfig.userStations, userSession, stationFilter)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleStationFilterChange = (event) => {
    dispatch(updateStationFilter(event.target.value));
    stationFilterRef.current = event.target.value;
  };

  const handleWorkgroupFilterChange = (event) => {
    let newWorkgroupFilterSelectedList = event.target.value;
    dispatch(updateWorkgroupFilters(newWorkgroupFilterSelectedList));
  };

  const renderStationDropDown = () => {
    return (
      <>
        {userSession.appRole === MULTI_STATION_ADMIN_ACCESS ? (
          <Box>
            <StarsSelectInput
              label={STATION}
              id="stationFilterSelectElement"
              menuMessage={STATION}
              value={stationFilter}
              allOptions={getMultiStationAdminStationList(userSession)}
              sortComparator={sortOnCaseSensitiveTexts}
              onChange={handleStationFilterChange}
              sx={{ width: "100px", marginRight: "1rem" }}
              isMenuItemDisabled={(station) => !props?.featureConfig?.userStations?.includes(station)}
              errors={stationFilter === "" ? STATION_FILTER_MUST_BE_SELECTED : ""}
            />
          </Box>
        ) : (
          <Box>
            <TextField
              label="Station"
              variant="outlined"
              value={stationFilter}
              sx={{ width: "100px", marginRight: "1rem" }}
              disabled
            />
          </Box>
        )}
      </>
    );
  };

  const renderWorkgroupFilterDropDown = () => {
    return (
      <StarsSelectInput
        label={"Workgroup"}
        id="workgroupFilterSelectElement"
        menuMessage="Filter on Workgroup"
        value={workgroupFilters}
        allOptions={ALL_WORKGROUP_DEFAULT_FILTERS}
        sortComparator={sortOnCaseSensitiveTexts}
        onChange={handleWorkgroupFilterChange}
        sx={{ width: "250px", textAlign: "left" }}
        errors={workgroupFilters.length <= 0 ? WORKGROUP_FILTER_MUST_BE_SELECTED : ""}
        fullWidth={true}
        multiple={true}
      />
    );
  };

  const renderPageHeader = () => {
    return (
      <>
        <Grid
          container
          alignItems="center"
          sx={
            props.isDesktopView
              ? { backgroundColor: "secondary.swaGray1", minHeight: "77px" }
              : { backgroundColor: "secondary.swaGray1", minHeight: "50px" }
          }
        >
          <Grid item xs={8}>
            <Typography component={"div"}>
              <Box
                sx={
                  props.isDesktopView
                    ? { fontSize: "32px", marginLeft: "3rem", textAlign: "left", color: "primary.swaBlue" }
                    : {
                        fontSize: "20px",
                        marginLeft: "1rem",
                        textAlign: "left",
                        color: "primary.swaBlue",
                        marginTop: "10px",
                      }
                }
              >
                {"Welcome, " + userSession.firstName + "!"}
              </Box>
            </Typography>
          </Grid>
          {activeTab === TABS.ADMIN_DASHBOARD && props.isDesktopView && (
            <Grid item xs={4} sx={{ display: "flex", justifyContent: "end" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  paddingRight: "3rem",
                  marginTop: "10px",
                  marginBottom: "10px",
                }}
              >
                {renderStationDropDown()}
                {props.isDesktopView && renderWorkgroupFilterDropDown()}
              </Box>
            </Grid>
          )}
        </Grid>
        <Divider sx={{ marginTop: 0 }} />
      </>
    );
  };

  return (
    <div data-testid="home-page">
      {renderPageHeader()}
      {isLoading ? (
        <PageLoading />
      ) : (
        <>
          {userSession.isAdmin && props.isDesktopView && (
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                centered
                sx={{
                  "& .MuiTab-root": {
                    fontSize: "14px",
                    fontWeight: "bold",
                  },
                }}
              >
                <Tab label="MY DASHBOARD" />
                <Tab label="ADMIN DASHBOARD" />
              </Tabs>
            </Box>
          )}
          <Box mt={props.isDesktopView && 1}>
            {userSession.isAdmin && props.isDesktopView ? (
              activeTab === TABS.MY_DASHBOARD ? (
                <EmployeeDashboard isDesktopView={props.isDesktopView} userFeatures={props.userFeatures} />
              ) : (
                <AdminDashboard
                  isDesktopView={props.isDesktopView}
                  workgroupFilters={workgroupFilters}
                  userFeatures={props.userFeatures}
                />
              )
            ) : (
              <EmployeeDashboard isDesktopView={props.isDesktopView} userFeatures={props.userFeatures} />
            )}
          </Box>
        </>
      )}
    </div>
  );
};

HomePage.propTypes = {
  userFeatures: PropTypes.array.isRequired,
  featureConfig: PropTypes.object.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
};

export default HomePage;
